import type { FormEvent, JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Input, Select, useToast } from '@artosera/ui';
import { estElevationAnnulee } from '../../components/useElevation.js';
import { inviterAssocie } from '../../lib/api.js';
import formStyles from '../../styles/form.module.css';
import { useMessageErreur } from '../Finances/commun.js';
import styles from './Associes.module.css';

/**
 * « Inviter un associé » : l'adresse, le nom, la langue du courriel.
 * L'invitation exige une vérification récente du second facteur : `executer`
 * ouvre la boîte d'élévation si l'API la demande, puis rejoue l'envoi. Fermer
 * cette boîte-là n'envoie rien et laisse ce formulaire ouvert.
 */
export function InvitationDialog({
  ouvert,
  onOuvertChange,
  executer,
  onInvite,
}: {
  ouvert: boolean;
  onOuvertChange: (ouvert: boolean) => void;
  executer: <T>(geste: () => Promise<T>) => Promise<T>;
  onInvite: () => void;
}): JSX.Element {
  const { t, i18n } = useTranslation('console');
  const { toast } = useToast();
  const message = useMessageErreur();
  const [email, setEmail] = useState('');
  const [nom, setNom] = useState('');
  const [langue, setLangue] = useState<'fr' | 'en'>(i18n.language.startsWith('en') ? 'en' : 'fr');
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const valide = email.trim() !== '' && nom.trim() !== '';

  const fermer = (): void => {
    setEmail('');
    setNom('');
    setErreur(null);
    onOuvertChange(false);
  };

  async function envoyer(evenement: FormEvent): Promise<void> {
    evenement.preventDefault();
    if (!valide) return;
    setErreur(null);
    setEnCours(true);
    try {
      const reponse = await executer(() =>
        inviterAssocie({ email: email.trim(), nom: nom.trim(), langue }),
      );
      toast({
        title: reponse.invitationEnvoyee
          ? t('associes.invitation.succes', { email: email.trim() })
          : t('associes.invitation.sansCourriel', { email: email.trim() }),
        tone: reponse.invitationEnvoyee ? 'default' : 'danger',
        closeLabel: t('saisies.suppression.fermer'),
      });
      onInvite();
      fermer();
    } catch (echec) {
      // Fermer la boîte d'élévation n'est pas une erreur : rien n'est parti.
      if (!estElevationAnnulee(echec)) setErreur(message(echec));
    } finally {
      setEnCours(false);
    }
  }

  return (
    <Dialog
      open={ouvert}
      onOpenChange={(estOuvert) => {
        if (!estOuvert && !enCours) fermer();
      }}
      title={t('associes.invitation.titre')}
      description={t('associes.invitation.texte')}
      closeLabel={t('saisies.suppression.fermer')}
      footer={
        <>
          <Button variant="ghost" onClick={fermer} disabled={enCours}>
            {t('saisies.annuler')}
          </Button>
          <Button type="submit" form="invitation-associe" loading={enCours} disabled={!valide}>
            {enCours ? t('associes.invitation.envoi') : t('associes.invitation.envoyer')}
          </Button>
        </>
      }
    >
      <form
        id="invitation-associe"
        className={styles.formulaireInvitation}
        onSubmit={(e) => void envoyer(e)}
        noValidate
      >
        <div className={formStyles.champ}>
          <label htmlFor="invitation-email">{t('associes.invitation.email')}</label>
          <Input
            id="invitation-email"
            type="email"
            autoComplete="off"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className={formStyles.champ}>
          <label htmlFor="invitation-nom">{t('associes.invitation.nom')}</label>
          <Input
            id="invitation-nom"
            autoComplete="off"
            maxLength={160}
            value={nom}
            onChange={(e) => setNom(e.target.value)}
          />
        </div>
        <div className={formStyles.champ}>
          <label htmlFor="invitation-langue">{t('associes.invitation.langue')}</label>
          <Select
            id="invitation-langue"
            value={langue}
            onValueChange={(valeur) => setLangue(valeur === 'en' ? 'en' : 'fr')}
            options={[
              { value: 'fr', label: t('nouvelleGalerie.langue.fr') },
              { value: 'en', label: t('nouvelleGalerie.langue.en') },
            ]}
          />
        </div>
        {erreur ? (
          <p className={formStyles.erreur} role="alert">
            {erreur}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}
